import IconButton from 'components/@extended/IconButton';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { CreateDialogRequiredBlock } from 'components/create-dialog/CreateDialogRequired';
import { useDraftSaveMode } from 'components/drafts/DraftSaveMode';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import NumberInput from 'components/forms/inputs/rhf-inputs/NumberInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import { PlusIcon, Trash2Icon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import { SIMPLE_TABLE_HEAD_CLASS } from 'components/tables/BasicTable';
import { Button } from 'components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { FieldDescription, FieldError } from 'components/ui/field';
import { useEffect, useRef } from 'react';
import { type FieldPath, useFieldArray, useFormState, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLazyGetActivePracticeAreasWithFiltersQuery } from 'store/api/practice-areas';
import { EffortForm, NominationLineForm, NominationWarning, RANKINGS_REFEREE_TYPE } from 'types/efforts';
import { cn } from 'utils/cn';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { NominationStatusSelect } from '../../rankings-referees/NominationStatusSelect';
import {
    NominationWarningNote,
    useNominationWarnings,
    useNominationWarningText,
} from '../../rankings-referees/nominationWarnings';
import { duplicateNominationLines, nominationLineToRequest } from '../effortFormMapping';
import { EffortFormState, nominationLineName } from './useEffortForm';

type FieldsProps = { form: EffortFormState };

const FRAME_CLASS = 'border-divider overflow-hidden rounded-md border';
const HEAD_CLASS = cn(SIMPLE_TABLE_HEAD_CLASS, 'px-3 first:pl-3 last:pr-3');

/**
 * Focus for a control that stays, once the one a removal or a last pick unmounted has gone: the modal or menu that
 * held the focus hands it back in a microtask after the commit, to whatever was focused before, and this runs after.
 */
const focusLater = (target: () => HTMLElement | null | undefined) =>
    requestAnimationFrame(() => setTimeout(() => target()?.focus()));

/**
 * One line: its directory edition, then its referees, each with their status and at most one warning. A line that
 * repeats another is framed red and says so.
 */
function NominationLineFields({
    form,
    index,
    warnings,
    duplicate,
    onRemove,
}: FieldsProps & { index: number; warnings: NominationWarning[]; duplicate: boolean; onRemove: () => void }) {
    const { control, setValue, getValues } = form.methods;
    const { classifiers, confirmRemoveNomination } = form;
    const watchedLine = useWatch({ control, name: `nominations.${index}` });
    const people = useWatch({ control, name: 'partyPersons' });
    const warningText = useNominationWarningText(classifiers['LEGAL_DIRECTORY']);
    const frame = useRef<HTMLDivElement>(null);
    if (!watchedLine) {
        return null;
    }
    const line = watchedLine;
    const lineName = nominationLineName(line, index);
    const onLine = new Set(line.referees.map(({ party }) => party.id));
    const addable = people.filter(({ id }) => !onLine.has(id));

    const rows = line.referees.map((referee) => {
        const warning = warnings.find((w) => w.lineIndex === index && w.partyId === referee.party.id);
        return { referee, warning: warning ? warningText(referee.party.name, warning) : null };
    });

    const setReferees = (referees: NominationLineForm['referees']) =>
        setValue(`nominations.${index}.referees`, referees, { shouldDirty: true });

    return (
        <div
            ref={frame}
            data-nomination-line={index}
            className={cn(FRAME_CLASS, 'flex flex-col gap-4 p-4', duplicate && 'border-destructive')}
        >
            <div className="flex items-start gap-3">
                <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)_minmax(0,1.3fr)] gap-3 max-sm:grid-cols-1">
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        name={`nominations.${index}.legalDirectory`}
                        label="efforts.nominations.directory"
                        placeholder="efforts.nominations.directory-placeholder"
                        tooltip={<InfoTooltip text="efforts.nominations.directory-tooltip" />}
                        options={
                            getValidOptionsFromClassifier(classifiers['LEGAL_DIRECTORY']) as {
                                label: string;
                                value: string;
                            }[]
                        }
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        noPreSelect
                        required
                    />
                    <NumberInput
                        control={control}
                        name={`nominations.${index}.editionYear`}
                        label="efforts.nominations.edition"
                        tooltip={<InfoTooltip text="efforts.nominations.edition-tooltip" />}
                        inputProps={{ step: 1 }}
                        // Not min/max, which clamp each keystroke. An empty value is the required rule's to judge.
                        validate={(value) =>
                            value === '' ||
                            value == null ||
                            (Number.isInteger(Number(value)) && Number(value) >= 1900 && Number(value) <= 2999) ||
                            t('efforts.nominations.edition-range')
                        }
                        required
                    />
                    <DynamicSelectInput
                        setValue={setValue}
                        control={control}
                        name={`nominations.${index}.practiceArea`}
                        label="efforts.nominations.practice-area"
                        placeholder="efforts.nominations.practice-area-placeholder"
                        tooltip={<InfoTooltip text="efforts.nominations.practice-area-tooltip" />}
                        paginated={{ fetch: useLazyGetActivePracticeAreasWithFiltersQuery, searchField: 'name' }}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                    />
                </div>
                <IconButton
                    type="button"
                    size="small"
                    className="mt-6"
                    aria-label={t('remove-name', { name: lineName })}
                    onClick={onRemove}
                >
                    <Trash2Icon className="size-4" />
                </IconButton>
            </div>
            {duplicate ? (
                <FieldError className="-mt-2">
                    <FormattedMessage id="efforts.nominations.duplicate-line" />
                </FieldError>
            ) : null}
            <div className="flex flex-col gap-1">
                <span className="text-body2 text-fg-secondary new:text-foreground-secondary flex items-center gap-1">
                    <FormattedMessage id="efforts.nominations.referees" />
                    <span className="text-destructive new:text-danger-ink">*</span>
                    <InfoTooltip text="efforts.nominations.referees-tooltip" />
                </span>
                {/* Mounted empty, before the warnings arrive, so each change to them is said. */}
                <div role="status" className="sr-only">
                    {rows
                        .map(({ warning }) => warning)
                        .filter(Boolean)
                        .join(' ')}
                </div>
                {/* A list, not a table: a row wraps its status and trash under the name where the line is narrow. */}
                <div className={cn(FRAME_CLASS, 'divide-divider new:divide-border flex flex-col divide-y')}>
                    <div
                        className={cn(
                            HEAD_CLASS,
                            'flex h-auto flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2'
                        )}
                    >
                        <span className="flex-[1_1_14rem]">
                            <FormattedMessage id="efforts.nominations.referee" />
                        </span>
                        <span className="inline-flex w-[176px] shrink-0 items-center gap-1">
                            <FormattedMessage id="efforts.nominations.status" />
                            <InfoTooltip text="efforts.nominations.status-tooltip" small />
                        </span>
                    </div>
                    {rows.length > 0 ? (
                        <ul className="divide-divider new:divide-border m-0 flex list-none flex-col divide-y p-0">
                            {rows.map(({ referee, warning }, row) => (
                                <li
                                    key={referee.party.id}
                                    className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-3 py-2"
                                >
                                    <div className="flex min-w-0 flex-[1_1_14rem] flex-col gap-1 pt-1">
                                        <span className="text-body1 font-medium">{referee.party.name}</span>
                                        {warning ? <NominationWarningNote text={warning} /> : null}
                                    </div>
                                    <span className="flex shrink-0 items-center gap-1">
                                        <NominationStatusSelect
                                            value={referee.statusCode}
                                            label={t('rankings-referees.status-of', {
                                                name: referee.party.name,
                                                nomination: lineName,
                                            })}
                                            onChange={(statusCode) =>
                                                setValue(
                                                    `nominations.${index}.referees.${row}.statusCode`,
                                                    statusCode,
                                                    {
                                                        shouldDirty: true,
                                                    }
                                                )
                                            }
                                        />
                                        <IconButton
                                            type="button"
                                            size="small"
                                            aria-label={t('efforts.nominations.remove-referee', {
                                                name: referee.party.name,
                                                nomination: lineName,
                                            })}
                                            onClick={() =>
                                                confirmRemoveNomination(referee, lineName, () => {
                                                    setReferees(
                                                        (getValues(`nominations.${index}.referees`) ?? []).filter(
                                                            ({ party }) => party.id !== referee.party.id
                                                        )
                                                    );
                                                    // The person is addable again, so the line's Add referee is there.
                                                    focusLater(() =>
                                                        frame.current?.querySelector<HTMLElement>(
                                                            '[data-add-referee] button'
                                                        )
                                                    );
                                                })
                                            }
                                        >
                                            <Trash2Icon className="size-4" />
                                        </IconButton>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    {addable.length > 0 ? (
                        <div data-add-referee className="px-3 py-1.5">
                            <DropdownMenu>
                                <DropdownMenuTrigger
                                    render={
                                        <Button
                                            type="button"
                                            {...buttonStyle(
                                                { variant: 'text', size: 'extraSmall' },
                                                '-ml-2 h-auto min-h-6 min-w-0 shrink gap-1 py-1 text-start whitespace-normal'
                                            )}
                                        />
                                    }
                                >
                                    <PlusIcon className="size-4 shrink-0" />
                                    <FormattedMessage id="efforts.nominations.add-referee" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    {addable.map((person) => (
                                        <DropdownMenuItem
                                            key={person.id}
                                            onClick={() => {
                                                setReferees([
                                                    ...(getValues(`nominations.${index}.referees`) ?? []),
                                                    {
                                                        party: { id: person.id, name: person.name },
                                                        statusCode: 'REQUESTED',
                                                    },
                                                ]);
                                                // The last pick takes this menu away: on to the new referee's status.
                                                if (addable.length === 1) {
                                                    focusLater(() =>
                                                        frame.current?.querySelector<HTMLElement>(
                                                            'li:last-of-type button'
                                                        )
                                                    );
                                                }
                                            }}
                                        >
                                            {person.name}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

/**
 * The Nominations section of a Rankings referee effort: one line a directory edition, each holding the referees
 * asked for it. In the create dialog the lines are one required field: the block moves into Required whole, so a
 * line's Directory and Edition stay on their line.
 */
export const NominationsFields = ({ form }: FieldsProps): JSX.Element => {
    const { control, getValues, trigger, clearErrors } = form.methods;
    const drafting = useDraftSaveMode();
    const addLine = useRef<HTMLButtonElement>(null);
    const ruleError = useRef<HTMLDivElement>(null);
    const handledSubmit = useRef(0);
    const inDialog = useInCreateDialog();
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'nominations',
        rules: {
            // No two lines may repeat one another, which the server refuses for a draft too. A draft saves without
            // lines; otherwise every line needs a referee, and the effort a line. The rule outlives the section, so
            // it asks the type too.
            validate: (lines, values) => {
                if (values.effortActivityType?.value !== RANKINGS_REFEREE_TYPE) return true;
                if (duplicateNominationLines(lines ?? []).size > 0) return t('efforts.nominations.duplicate-lines');
                return (
                    drafting ||
                    (!!lines?.length && lines.every(({ referees }) => referees.length > 0)) ||
                    t('efforts.nominations.required-line')
                );
            },
        },
    });
    const lines = useWatch({ control, name: 'nominations' }) ?? [];
    const { errors, submitCount, isSubmitting } = useFormState({ control, name: 'nominations' });
    // A draft save turns the required rules off for itself alone: a re-check after it would ask for them again.
    const draftSave = useRef(false);
    useEffect(() => {
        if (isSubmitting) draftSave.current = drafting;
    }, [isSubmitting, drafting]);
    // Once a save has asked, the rule follows every change to the lines, so their marks and its message clear together.
    // After a draft, the duplicates are all it can have refused.
    const linesKey = JSON.stringify(lines);
    useEffect(() => {
        if (submitCount === 0) return;
        if (!draftSave.current) void trigger('nominations');
        else if (duplicateNominationLines(getValues('nominations') ?? []).size === 0)
            // The lines' own error sits at `<array>.root`, a path the type does not list.
            clearErrors('nominations.root' as FieldPath<EffortForm>);
    }, [linesKey, submitCount, trigger, getValues, clearErrors]);
    const duplicates = errors.nominations?.root ? duplicateNominationLines(lines) : new Set<number>();
    // The lines' rule has no field to focus: when it is all a Save failed on, its message takes the focus instead.
    useEffect(() => {
        const ruleAlone =
            Object.keys(errors).join() === 'nominations' && Object.keys(errors.nominations ?? {}).join() === 'root';
        if (submitCount > handledSubmit.current && ruleAlone) {
            handledSubmit.current = submitCount;
            ruleError.current?.scrollIntoView({ block: 'center' });
            ruleError.current?.focus();
        }
    }, [errors, submitCount]);
    const effortId = getValues('id');
    const effortDate = useWatch({ control, name: 'effortDate' });
    const warnings = useNominationWarnings({
        effortId: effortId ? Number(effortId) : null,
        // A date still being typed is stored empty: the server then counts from today.
        effortDate: effortDate || null,
        lines: lines.map(nominationLineToRequest),
    });
    const hasCompleteLine = lines.some(
        (line) => line.legalDirectory && line.editionYear !== null && line.editionYear !== '' && line.referees.length
    );

    return (
        <CreateDialogRequiredBlock when isEmpty={!hasCompleteLine}>
            <div className="col-span-full flex w-full flex-col gap-4">
                {/* The dialog draws the section's title as its toggle, which cannot hold the tooltip: this does. */}
                {inDialog ? (
                    <span className="text-foreground-secondary flex items-center gap-1 text-xs font-medium">
                        <FormattedMessage id="efforts.nominations.title" />
                        <span className="text-destructive new:text-danger-ink">*</span>
                        <InfoTooltip text="efforts.nominations.title-tooltip" small />
                    </span>
                ) : null}
                {fields.map((field, index) => (
                    <NominationLineFields
                        key={field.id}
                        form={form}
                        index={index}
                        warnings={warnings}
                        duplicate={duplicates.has(index)}
                        onRemove={() =>
                            form.confirmRemoveLine(lines[index], index, () => {
                                remove(index);
                                focusLater(() => addLine.current);
                            })
                        }
                    />
                ))}
                <FieldError ref={ruleError} tabIndex={-1}>
                    {errors.nominations?.root?.message}
                </FieldError>
                <FieldDescription className="text-xs">
                    <FormattedMessage id="efforts.nominations.referees-hint" />
                </FieldDescription>
                <Button
                    ref={addLine}
                    type="button"
                    {...buttonStyle({ variant: 'outlined' }, 'w-fit')}
                    onClick={() => append(form.newLine())}
                >
                    <FormattedMessage id="efforts.nominations.add-line" />
                </Button>
            </div>
        </CreateDialogRequiredBlock>
    );
};
